// Updated: 2026-08-21 10:40:00 CDT
// Purpose: Configure persistent public-app playback preferences outside the screenplay editor.

import { useEffect, useState, type ReactNode } from "react";
import { Settings } from "lucide-react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { loadStoredVoiceCatalog } from "@/utils/storedVoiceCatalog";
import { resolvePreviewVoice } from '@/utils/previewVoice';
import { parseInterfaceTextSize, useInterfaceTextSize } from '@/contexts/InterfaceTextSize';
import { parseScreenplayBackground, type ScreenplayBackground } from "@/utils/screenplayAppearance";
import { SPEECH_LANGUAGES, restoreSpeechLanguage, type SpeechLanguage } from '@/utils/speechLanguage';
import { useModifierDisplay } from '@/contexts/ModifierDisplay';

function SettingsRow({id,children,description,title}:{id:string;children:ReactNode;description:ReactNode;title?:string}){
  return <tr><td id={`${id}-description`} className="space-y-2 text-sm text-muted-foreground">{title&&<Label htmlFor={id} className="block text-foreground">{title}</Label>}{description}</td><td><div className="space-y-2">{children}</div></td></tr>;
}

interface PreviewVoice {
  id: string;
  display_name: string;
  is_narrator: boolean;
}

interface PublicSettingsProps {
  sayCharacterName?: boolean;
  onSayCharacterNameChange?: (enabled: boolean) => void;
  playbackPreferencesDisabled?: boolean;
  previewVoiceId: string;
  onPreviewVoiceChange: (voiceId: string) => void;
  screenplayBackground: ScreenplayBackground;
  onScreenplayBackgroundChange: (background: ScreenplayBackground) => void;
  screenplaySpellcheck: boolean;
  onScreenplaySpellcheckChange: (enabled: boolean) => void;
  defaultSpeechLanguage?: SpeechLanguage;
  onDefaultSpeechLanguageChange?: (language: SpeechLanguage) => void;
  scriptLanguage?: SpeechLanguage;
  scriptFilename?: string | null;
  onScriptLanguageChange?: (language: SpeechLanguage) => void;
  scriptLanguageSaving?: boolean;
}

export default function PublicSettings({
  sayCharacterName = false, onSayCharacterNameChange, playbackPreferencesDisabled = false,
  previewVoiceId,
  onPreviewVoiceChange,
  screenplayBackground,
  onScreenplayBackgroundChange,
  screenplaySpellcheck,
  onScreenplaySpellcheckChange,
  defaultSpeechLanguage = 'en', onDefaultSpeechLanguageChange,
  scriptLanguage = 'auto', scriptFilename, onScriptLanguageChange,
  scriptLanguageSaving = false,
}: PublicSettingsProps) {
  const {size: interfaceTextSize, setSize: setInterfaceTextSize} = useInterfaceTextSize();
  const modifierDisplay=useModifierDisplay();
  const [voices, setVoices] = useState<PreviewVoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadVoices = async () => {
      setIsLoading(true);
      try {
        const availableVoices = await loadStoredVoiceCatalog<PreviewVoice>(supabase, "id,display_name,is_narrator");
        if (cancelled) return;
        setVoices(availableVoices);

        if (
          availableVoices.length > 0 &&
          !availableVoices.some((voice) => voice.id === previewVoiceId)
        ) {
          onPreviewVoiceChange(resolvePreviewVoice(availableVoices, previewVoiceId));
        }

        console.debug("[PublicSettings] Preview voices loaded:", {
          voiceCount: availableVoices.length,
          preferredVoiceAvailable: availableVoices.some(
            (voice) => voice.id === previewVoiceId,
          ),
        });
      } catch {
        if (!cancelled) toast.error("Unable to load the full voice catalog. Please refresh.");
      }

      if (!cancelled) setIsLoading(false);
    };

    void loadVoices();
    return () => {
      cancelled = true;
    };
  }, [onPreviewVoiceChange, previewVoiceId]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <Settings className="h-5 w-5 text-primary" />
          <div>
            <CardTitle>Settings</CardTitle>
            <CardDescription>
              Configure playback and editor preferences for this browser.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <table className="public-settings-table w-full table-fixed border-collapse" aria-label="Playback and editor settings">
        <colgroup><col className="w-[55%]"/><col className="w-[45%]"/></colgroup>
        <thead><tr><th scope="col">Description</th><th scope="col">Control</th></tr></thead><tbody>
        <SettingsRow id="say-character-name" description="Announce each character’s name before their dialogue during playback and MP3 export.">
            <input id="say-character-name" type="checkbox" checked={sayCharacterName} disabled={playbackPreferencesDisabled} aria-label="Say character name" aria-describedby="say-character-name-description"
              onChange={event=>onSayCharacterNameChange?.(event.target.checked)}/>
        </SettingsRow>
        <SettingsRow id="default-speech-language" title="Default speech language for new screenplays" description="Saves in this browser for your account. Language controls how the written text is spoken; it does not translate it or change the chosen voice’s accent.">
          <select id="default-speech-language" value={defaultSpeechLanguage} className="h-10 w-full rounded-md border bg-background px-3" aria-describedby="default-speech-language-description"
            onChange={e => onDefaultSpeechLanguageChange?.(restoreSpeechLanguage(e.target.value))}>
            <option value="auto">Automatic detection</option>
            {Object.entries(SPEECH_LANGUAGES).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
        </SettingsRow>
          {scriptFilename && <SettingsRow id="current-speech-language" title="Current screenplay language" description={<>Saved with {scriptFilename}. Changing this can require new audio for the screenplay, subject to existing limits. Individual blocks can override this language in the editor. Explicit choices apply to streaming, ordinary and Batch requests; automatic detection has no language lock.</>}>
            <select id="current-speech-language" value={scriptLanguage} className="h-10 w-full rounded-md border bg-background px-3" aria-describedby="current-speech-language-description"
              disabled={scriptLanguageSaving}
              onChange={e => onScriptLanguageChange?.(restoreSpeechLanguage(e.target.value))}>
              <option value="auto">Automatic detection (legacy-compatible)</option>
              {Object.entries(SPEECH_LANGUAGES).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
            </select>
            {scriptLanguageSaving && <p role="status" className="text-sm">Saving screenplay language…</p>}
          </SettingsRow>}
        <SettingsRow id="tableread-screenplay-spellcheck" description="Uses your browser’s spelling checker in screenplay fields. Availability and language depend on your browser settings. This preference saves automatically in this browser.">
          <label className="flex items-center gap-3 text-sm font-medium" htmlFor="tableread-screenplay-spellcheck">
            <input id="tableread-screenplay-spellcheck" type="checkbox" checked={screenplaySpellcheck} aria-describedby="tableread-screenplay-spellcheck-description"
              onChange={event => onScreenplaySpellcheckChange(event.target.checked)} />
            Underline misspelled words
          </label>
        </SettingsRow>
        <SettingsRow id="tableread-screenplay-background" description="Changes only the script editor background and saves automatically in this browser.">
          <Label className="sr-only" htmlFor="tableread-screenplay-background">Script editor background</Label>
          <Select value={screenplayBackground} onValueChange={(value) => onScreenplayBackgroundChange(parseScreenplayBackground(value))}>
            <SelectTrigger id="tableread-screenplay-background" aria-describedby="tableread-screenplay-background-description"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="dark">Dark (default)</SelectItem>
              <SelectItem value="white">White</SelectItem>
            </SelectContent>
          </Select>
        </SettingsRow>
        <SettingsRow id="tableread-modifier-display" description="Choose how text with emotion or delivery modifiers is marked. This browser-only preference does not change screenplay text, saved attributes or audio.">
          <Label className="sr-only" htmlFor="tableread-modifier-display">Text with modifiers</Label>
          <select id="tableread-modifier-display" value={modifierDisplay.mode} aria-describedby="tableread-modifier-display-description" className="h-10 w-full rounded-md border bg-background px-3"
            onChange={event=>modifierDisplay.setMode(event.target.value==='highlighted'?'highlighted':'bracketed')}>
            <option value="bracketed">Bracketed (default)</option><option value="highlighted">Highlighted</option>
          </select>
        </SettingsRow>
        {modifierDisplay.mode==='highlighted'&&<SettingsRow id="tableread-modifier-color" description="Select the highlight color. A translucent tint keeps the script readable on either background. Your color is retained when switching display modes.">
          <Label className="sr-only" htmlFor="tableread-modifier-color">Modifier highlight color</Label>
          <div className="flex items-center gap-3"><input id="tableread-modifier-color" type="color" value={modifierDisplay.color} aria-describedby="tableread-modifier-color-description" className="h-10 w-16 cursor-pointer rounded border bg-background p-1" onInput={event=>modifierDisplay.setColor(event.currentTarget.value)} onChange={event=>modifierDisplay.setColor(event.target.value)}/><span className="font-mono text-sm">{modifierDisplay.color}</span></div>
        </SettingsRow>}
        <SettingsRow id="tableread-preview-voice" title="TableRead preview voice" description="Assigned character voices take priority. This voice is used whenever a screenplay role has no assignment.">
          <Select
            value={previewVoiceId}
            onValueChange={onPreviewVoiceChange}
            disabled={isLoading || voices.length === 0}
          >
            <SelectTrigger id="tableread-preview-voice" aria-describedby="tableread-preview-voice-description">
              <SelectValue
                placeholder={isLoading ? "Loading voices…" : "Choose a voice"}
              />
            </SelectTrigger>
            <SelectContent>
              {voices.map((voice) => (
                <SelectItem key={voice.id} value={voice.id}>
                  {voice.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SettingsRow>

        <SettingsRow id="tableread-interface-text-size" title="Interface text size" description="Adjusts menus, buttons and other interface text. Saves automatically in this browser. Use Zoom in the editor to resize the script text separately.">
          <Select
            value={interfaceTextSize}
            onValueChange={(value) => setInterfaceTextSize(parseInterfaceTextSize(value))}
          >
            <SelectTrigger id="tableread-interface-text-size" aria-describedby="tableread-interface-text-size-description">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="small">Small</SelectItem>
              <SelectItem value="medium">Medium (default)</SelectItem>
              <SelectItem value="large">Large</SelectItem>
            </SelectContent>
          </Select>
        </SettingsRow>
        </tbody></table>
      </CardContent>
    </Card>
  );
}
